import { DatePipe, registerLocaleData } from '@angular/common';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import localesEs from '@angular/common/locales/es';
import {
  APP_INITIALIZER,
  CUSTOM_ELEMENTS_SCHEMA,
  LOCALE_ID,
  NgModule,
  ErrorHandler
} from '@angular/core';
import * as Sentry from '@sentry/angular';
import { Router } from '@angular/router';
import { AngularFireModule } from '@angular/fire';
import {
  AngularFireAnalytics,
  AngularFireAnalyticsModule
} from '@angular/fire/analytics';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSelectModule } from '@angular/material/select';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { KeycloakAngularModule, KeycloakService } from 'keycloak-angular';
import { NgCircleProgressModule } from 'ng-circle-progress';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { NgxSpinnerModule } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_PROVIDERS } from './app.providers';
import { ComponentsModule } from './components/components.module';
import { DialogsModule } from './dialogs/dialogs.module';
import { NotificationMessageComponent } from './dialogs/notification-message/notification-message.component';
import { LogService } from './services/log.service';
import { LoggerManagerService } from './services/logger-manager.service';
registerLocaleData(localesEs);

function initializeKeycloak(keycloak: KeycloakService): () => Promise<boolean> {
  return () =>
    keycloak.init({
      config: {
        url: environment.keycloak.url,
        realm: environment.keycloak.realm,
        clientId: environment.keycloak.clientId,
      },
      initOptions: {
        onLoad: 'login-required',
        checkLoginIframe: false,
        // redirectUri: environment.keycloak.redirectUri
        // onLoad: 'check-sso',
        // silentCheckSsoRedirectUri: window.location.origin + '/assets/silent-check-sso.html'
      },
    });
}
function initializeLoggerService(
  googleAnalytics: AngularFireAnalytics
): LoggerManagerService {
  return new LoggerManagerService(googleAnalytics, environment.loggerStrategy);
}
@NgModule({
  declarations: [AppComponent, NotificationMessageComponent],
  imports: [
    BrowserModule,
    MatSidenavModule,
    MatSelectModule,
    ComponentsModule,
    AppRoutingModule,
    BrowserAnimationsModule,
    HttpClientModule,
    KeycloakAngularModule,
    MatDialogModule,
    MatFormFieldModule,
    ReactiveFormsModule,
    FormsModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: (http: HttpClient) => {
          return new TranslateHttpLoader(http);
        },
        deps: [HttpClient],
      },
    }),
    MatToolbarModule,
    MatButtonModule,
    MatProgressBarModule,
    MatSnackBarModule,
    MatIconModule,
    MatMenuModule,
    MatExpansionModule,
    DialogsModule,
    MatTooltipModule,
    MatInputModule,
    InfiniteScrollModule,
    MatRippleModule,
    NgxSpinnerModule,
    NgCircleProgressModule.forRoot({
      backgroundStrokeWidth: 9,
      radius: 60,
      space: -16,
      unitsFontSize: '10',
      outerStrokeGradient: false,
      outerStrokeWidth: 16,
      outerStrokeLinecap: 'butt',
      innerStrokeWidth: 16,
      titleFontSize: '15',
      animateTitle: true,
      animationDuration: 1000,
      showUnits: false,
      clockwise: false,
      responsive: true,
      startFromZero: false,
      lazy: false,
      titleFontWeight: '800',
      subtitleFontWeight: '600',
    }),
    AngularFireModule.initializeApp(environment.firebase),
    AngularFireAnalyticsModule,
  ],
  providers: [
    DatePipe,
    LogService,
    APP_PROVIDERS,
    { provide: MAT_DATE_LOCALE, useValue: 'es-MX' },
    { provide: LOCALE_ID, useValue: 'es' },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeKeycloak,
      multi: true,
      deps: [KeycloakService],
    },
    {
      provide: LoggerManagerService,
      useFactory: initializeLoggerService,
      deps: [AngularFireAnalytics],
    },
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler({
        showDialog: false,
      }),
    },
    {
      provide: Sentry.TraceService,
      deps: [Router],
    },
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  bootstrap: [AppComponent],
})
export class AppModule {constructor(traceService: Sentry.TraceService) {} }
